<template>
  <!--
    FeaturedProjects — Home_Page featured projects section sourced live from the
    Projects API (Requirement 4.3). Project content always comes from the API,
    never from hardcoded markup (Requirements 11.1, 11.3); only the section's
    heading/intro/empty copy come from the Home content module.

    The section degrades gracefully: while loading it shows a lightweight
    placeholder, on error or empty result it shows the empty-state copy, and it
    never blocks the Home_Page from rendering its other sections.
  -->
  <section class="featured" aria-labelledby="featured-heading">
    <v-container>
      <ScrollReveal tag="div" class="featured__head">
        <h2 id="featured-heading" class="featured__heading">
          {{ content.heading }}
        </h2>
        <p v-if="content.intro" class="featured__intro">{{ content.intro }}</p>
      </ScrollReveal>

      <!-- Loading state: skeleton placeholders. -->
      <v-row v-if="status === 'loading'" aria-hidden="true">
        <v-col v-for="n in 3" :key="n" cols="12" sm="6" md="4">
          <v-skeleton-loader type="image, article" />
        </v-col>
      </v-row>

      <!-- Ready state: featured project cards. -->
      <v-row v-else-if="status === 'ready'">
        <v-col
          v-for="(project, index) in projects"
          :key="project.id || index"
          cols="12"
          sm="6"
          md="4"
        >
          <ScrollReveal tag="div" :delay="index * 60">
            <v-card
              class="featured__card"
              height="100%"
              :to="project.id ? `/projects/${project.id}` : undefined"
            >
              <v-img
                v-if="project.imageUrl"
                :src="project.imageUrl"
                :alt="project.imageAlt"
                height="200"
                cover
                loading="lazy"
              />
              <div v-else class="featured__placeholder" role="img" :aria-label="project.imageAlt">
                <span class="featured__placeholder-text">ThinkSpatial</span>
              </div>

              <v-card-item>
                <v-card-title class="featured__card-title">
                  {{ project.title }}
                </v-card-title>
                <v-card-subtitle class="featured__card-category">
                  {{ project.serviceCategory }}
                </v-card-subtitle>
              </v-card-item>

              <v-card-text v-if="project.summary" class="featured__card-summary">
                {{ project.summary }}
              </v-card-text>
            </v-card>
          </ScrollReveal>
        </v-col>
      </v-row>

      <!-- Empty / error state: the section stays present with friendly copy. -->
      <p v-else class="featured__empty">{{ content.emptyText }}</p>
    </v-container>
  </section>
</template>

<script>
// FeaturedProjects — API-sourced featured projects (Requirement 4.3).
import ScrollReveal from 'components/ScrollReveal.vue'
import ProjectsApi from 'services/ProjectsApi'
import { normalizeProjects } from 'services/projectNormalizer'
import home from 'src/content/home'

// Number of featured projects to surface on the Home_Page.
const FEATURED_LIMIT = 3

export default {
  name: 'FeaturedProjects',
  components: { ScrollReveal },
  data () {
    return {
      content: home.featuredProjects,
      projects: [],
      // 'loading' | 'ready' | 'empty' | 'error'
      status: 'loading'
    }
  },
  created () {
    this.loadProjects()
  },
  methods: {
    loadProjects () {
      this.status = 'loading'
      ProjectsApi.listWebsiteProjects()
        .then((response) => {
          const raw = (response && response.data && response.data.projects) || []
          const normalized = normalizeProjects(raw)
          this.projects = normalized.slice(0, FEATURED_LIMIT)
          this.status = this.projects.length > 0 ? 'ready' : 'empty'
        })
        .catch(() => {
          // Never block the Home_Page: fall back to the empty-state copy.
          this.projects = []
          this.status = 'error'
        })
    }
  }
}
</script>

<style scoped>
.featured {
  padding: 72px 0;
  background: #f7f6f5;
}

.featured__head {
  text-align: center;
  max-width: 720px;
  margin: 0 auto 40px;
}

.featured__heading {
  font-size: clamp(1.8rem, 4vw, 2.6rem);
  font-weight: 800;
  margin-bottom: 12px;
  color: #41281b;
}

.featured__intro {
  font-size: 1.1rem;
  line-height: 1.6;
  color: #4a4a4a;
}

.featured__card {
  transition: box-shadow 200ms ease, transform 200ms ease;
}

.featured__card:hover {
  box-shadow: 0 10px 30px rgba(65, 40, 27, 0.15);
  transform: translateY(-2px);
}

.featured__placeholder {
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #41281b 0%, #e7511e 100%);
}

.featured__placeholder-text {
  color: #fff;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.85;
}

.featured__card-title {
  font-weight: 700;
  color: #41281b;
  white-space: normal;
}

.featured__card-category {
  color: #e7511e;
  font-weight: 600;
  opacity: 1;
}

.featured__card-summary {
  color: #4a4a4a;
  line-height: 1.6;
}

.featured__empty {
  text-align: center;
  color: #4a4a4a;
  font-size: 1.05rem;
  padding: 24px 0;
}
</style>
